import { Modal, message, Button } from 'antd'
import { useState } from 'react'
import { useAuth } from '@/providers/auth.provider'
import { transformCollaborator } from '../TimeAndAction/TimeAction.helper'
import { getUrlParams } from '@/utils/common'
import { removeCollaborator } from '../../../Order.transform'
import {
  updateCollaboratorRef,
  updateCollaboratorRoleRef,
} from '../../../Order.service'
import { ACTING_ROLE_ADMIN, ActingUserRole } from '../../../Order.constant'
import InvitePeopleForm from './components/InvitePeopleForm/InvitePeopleForm'
import { queryClient } from '@/config/react-query'

type Props = {
  open: boolean
  onClose: () => void
  collaborators: any[]
  order: any
  actingUserRole: ActingUserRole
  isWorkspaceAdmin: boolean
  onUpdate: () => void
}

function InvitePeopleModal(props: Props) {
  const { open, onClose, order, actingUserRole, isWorkspaceAdmin } = props
  const { user } = useAuth()

  const flatCollaborators = transformCollaborator(
    props.collaborators,
    user,
    order
  )

  const [stagedCollaborators, setStagedCollaborators] =
    useState(flatCollaborators)
  const [removedCollaborators, setRemovedCollaborators] = useState([])
  const [isSaving, setIsSaving] = useState(false)

  const handleUpdate = async () => {
    setIsSaving(true)
    try {
      const orderId = getUrlParams('orderKey')

      // 1. Process Removals
      for (const removed of removedCollaborators) {
        if (removed.assignmentId) {
          await removeCollaborator(orderId, removed)
        }
      }

      // 2. Process Added (no assignmentId)
      const added = stagedCollaborators.filter((c) => !c.assignmentId)
      for (const c of added) {
        await updateCollaboratorRef(orderId, {
          roles: { role: c.role, permissions: [] },
          user_id: c.id,
        })
      }

      // 3. Process Updated (role changed)
      const updated = stagedCollaborators.filter((c) => {
        if (!c.assignmentId) return false
        const original = flatCollaborators.find(
          (o) => o.assignmentId === c.assignmentId
        )
        return original && original.role !== c.role
      })
      for (const c of updated) {
        await updateCollaboratorRoleRef(orderId, {
          roles: { role: c.role, permissions: [] },
          user_id: c.id,
        })
      }

      await queryClient.refetchQueries('ORDER_DETAIL')
      props.onUpdate()
      message.success('Collaboration updated.')
      onClose()
    } catch (e) {
      message.error('Failed to update collaboration.')
      try {
       await queryClient.refetchQueries('ORDER_DETAIL')
      } finally {
        onClose()
      }
    } finally {
      setIsSaving(false)
    }
  }

  const handleCancel = () => {
    setStagedCollaborators(flatCollaborators)
    setRemovedCollaborators([])
    onClose()
  }

  const resolvedRole = isWorkspaceAdmin ? ACTING_ROLE_ADMIN : actingUserRole

  return (
    <Modal
      className="pbf-invite-modal"
      title={null}
      open={open}
      onCancel={handleCancel}
      width={700}
      destroyOnClose
      footer={
        <div style={{ display: 'flex', gap: '16px', marginTop: '24px' }}>
          <Button
            key="cancel"
            onClick={handleCancel}
            style={{ flex: 1, height: '40px', borderRadius: '8px', fontSize: 16, fontWeight: 600, border: '1.5px solid #848484', color: '#848484' }}
          >
            Cancel
          </Button>
          <Button
            key="update"
            type="primary"
            onClick={handleUpdate}
            loading={isSaving}
            style={{ flex: 1, height: '40px', borderRadius: '8px', fontSize: 16, fontWeight: 600, backgroundColor: '#EF9747' }}
          >
            Update
          </Button>
        </div>
      }
    >
      <InvitePeopleForm
        onCollaborator={setStagedCollaborators}
        isImport={false}
        order={order}
        collaborator={stagedCollaborators}
        onRemoveCollaborator={(value) =>
          setRemovedCollaborators([...removedCollaborators, value])
        }
        actingUserRole={resolvedRole}
      />
    </Modal>
  )
}

export default InvitePeopleModal
